/**
 * Normalisation des données DPE depuis l'API ADEME
 * 
 * VERSION 2 - COMPATIBILITÉ LEGACY
 * 
 * IMPORTANT : Ce normalizer préserve les valeurs null/undefined pour les champs
 * de déperditions thermiques, car ils sont utilisés par enrichDPEWithEstimates()
 * pour détecter si on doit utiliser :
 * - Le NOUVEAU système (calculs fins avec données réelles)
 * - L'ANCIEN système (coefficients standards pour DPE legacy)
 * 
 * RÈGLE : 
 * - Champs CRITIQUES (déperditions, besoins) → null si absent (pas de fallback 0)
 * - Champs NON-CRITIQUES (surface, classes) → fallback si absent
 */

export interface NormalizedDPEData {
  // ============================================================================
  // IDENTIFIANTS & DATES
  // ============================================================================
  numero_dpe: string;
  date_etablissement_dpe: string;
  date_visite_diagnostiqueur: string | null;
  
  // ============================================================================
  // SURFACE & STRUCTURE (NON-CRITIQUES - avec fallback)
  // ============================================================================
  living_area: number; // Fallback 0 car toujours utilisé dans calculs
  surface_habitable_immeuble: number | null;
  building_type: 'maison' | 'appartement';
  annee_construction: number | null;

  // Surfaces réelles issues du DPE ADEME (null si non renseignées)
  surface_murs: number | null;               // Surface totale des murs extérieurs (m²)
  surface_planchers_hauts: number | null;    // Surface planchers hauts / toiture (m²)
  surface_planchers_bas: number | null;      // Surface planchers bas (m²)
  surface_baies_vitrees_total: number | null; // Surface totale des baies vitrées (m²) — somme des 3 orientations
  
  // ============================================================================
  // ZONE CLIMATIQUE (NON-CRITIQUE - avec fallback)
  // ============================================================================
  climate_zone: string; // Fallback 'H2'
  
  // ============================================================================
  // ISOLATION (NON-CRITIQUE - avec fallback)
  // ============================================================================
  wall_insulation: string; // Fallback 'moyenne'
  roof_insulation: string;
  floor_insulation: string;
  windows_type: string;
  
  // Champs originaux conservés
  qualite_isolation_murs: string;
  qualite_isolation_menuiseries: string;
  qualite_isolation_plancher_bas: string;
  qualite_isolation_plancher_haut_comble_amenage: string;
  qualite_isolation_plancher_haut_comble_perdu: string;
  qualite_isolation_plancher_haut_toit_terrasse: string;

  // Détection de type de toiture
  has_flat_roof: boolean;
  has_attic: boolean;
  
  // ============================================================================
  // DÉPERDITIONS (CRITIQUES - null si absent, pas de fallback 0 !)
  // ============================================================================
  deperditions_murs: number | undefined;
  deperditions_baies_vitrees: number | undefined;
  deperditions_planchers_hauts: number | undefined;
  deperditions_planchers_bas: number | undefined;
  deperditions_toiture: number | undefined;
  deperditions_ponts_thermiques: number | undefined;
  deperditions_enveloppe: number | undefined;
  deperditions_renouvellement_air: number | undefined;
  
  // ============================================================================
  // CHAUFFAGE (NON-CRITIQUE - avec fallback)
  // ============================================================================
  heating_energy: string;
  heating_type: string;
  heating_system: string;
  
  // Champs originaux
  type_energie_principale_chauffage: string;
  type_generateur_chauffage_principal: string;
  description_generateur_chauffage_n1_installation_n1: string;
  
  // ============================================================================
  // EAU CHAUDE SANITAIRE (NON-CRITIQUE - avec fallback)
  // ============================================================================
  hot_water_energy: string;
  hot_water_type: string;
  
  // Champs originaux
  type_energie_principale_ecs: string;
  type_generateur_chauffage_principal_ecs: string;
  
  // ============================================================================
  // VENTILATION (NON-CRITIQUE - avec fallback)
  // ============================================================================
  ventilation_system: string;
  type_ventilation: string;
  
  // ============================================================================
  // CONSOMMATIONS (CRITIQUES - null si absent pour certains)
  // ============================================================================
  primary_energy_consumption: number; // Fallback 0 car utilisé partout
  conso_5_usages_par_m2_ep: number;
  conso_5_usages_ef: number | undefined; // CRITIQUE pour détection chauffage
  conso_5_usages_par_m2_ef: number;
  
  conso_chauffage_ef: number | undefined; // CRITIQUE
  conso_ecs_ef: number | undefined; // CRITIQUE
  
  // BESOINS (CRITIQUES - null si absent)
  besoin_chauffage: number | undefined; // CRITIQUE pour enrichDPEWithEstimates
  besoin_ecs: number | undefined; // CRITIQUE pour enrichDPEWithEstimates
  
  // ============================================================================
  // ÉMISSIONS GES (NON-CRITIQUE - avec fallback)
  // ============================================================================
  greenhouse_gas_emissions: number;
  emission_ges_5_usages: number;

  // Émissions par usage (en kgCO2/an total, pas par m²)
  emission_ges_chauffage: number | undefined;
  emission_ges_ecs: number | undefined;
  emission_ges_eclairage: number | undefined;
  emission_ges_auxiliaires: number | undefined;

  // ============================================================================
  // CLASSES ÉNERGÉTIQUES (NON-CRITIQUE - avec fallback)
  // ============================================================================
  energy_class: string;
  climate_class: string;
  etiquette_dpe: string;
  etiquette_ges: string;
}

/**
 * Parse un JSON string qui peut être double-encodé
 * Gère les cas où heating_system ou hot_water_system sont des strings JSON escapées
 */
function parseJSONField(value: any): any {
  if (!value || typeof value !== 'string') {
    return value;
  }

  try {
    // Tentative de parsing si c'est du JSON
    const parsed = JSON.parse(value);

    // Si c'est encore une string après parsing, réessayer (double-encodage)
    if (typeof parsed === 'string') {
      try {
        return JSON.parse(parsed);
      } catch {
        return parsed;
      }
    }

    return parsed;
  } catch {
    // Pas du JSON, retourner tel quel
    return value;
  }
}

/**
 * Parse un nombre depuis une valeur brute
 * Retourne undefined si la valeur n'existe pas (préserve null/undefined)
 */
function parseNumberOrUndefined(value: any): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const parsed = parseFloat(String(value).replace(',', '.'));
  return isNaN(parsed) ? undefined : parsed;
}

/**
 * Parse un nombre avec fallback
 * À utiliser UNIQUEMENT pour les champs NON-CRITIQUES
 */
function parseNumberWithFallback(value: any, fallback: number): number {
  const parsed = parseNumberOrUndefined(value);
  return parsed !== undefined ? parsed : fallback;
}

/**
 * Normalise les qualités d'isolation
 */
function normalizeQuality(quality: string | null | undefined): string {
  if (!quality) return 'moyenne';
  const q = quality.toLowerCase().trim();
  
  if (q.includes('insuffisante') || q.includes('faible') || q.includes('mauvaise')) {
    return 'insuffisante';
  }
  if (q.includes('moyenne')) {
    return 'moyenne';
  }
  if (q.includes('bonne') && !q.includes('très')) {
    return 'bonne';
  }
  if (q.includes('très') || q.includes('tres') || q.includes('excellente')) {
    return 'tres_bonne';
  }
  
  return 'moyenne';
}

/**
 * Normalise les données brutes de l'API ADEME vers un format unifié
 * 
 * IMPORTANT : Préserve les undefined pour les champs critiques utilisés
 * par enrichDPEWithEstimates() pour détecter DPE legacy vs nouveau
 */
export function normalizeDPEData(rawDPE: any): NormalizedDPEData {
  if (!rawDPE) {
    throw new Error('normalizeDPEData: rawDPE is null or undefined');
  }

  return {
    // ============================================================================
    // IDENTIFIANTS & DATES
    // ============================================================================
    numero_dpe: rawDPE.numero_dpe || '',
    date_etablissement_dpe: rawDPE.date_etablissement_dpe || '',
    date_visite_diagnostiqueur: rawDPE.date_visite_diagnostiqueur || null,
    
    // ============================================================================
    // SURFACE & STRUCTURE
    // ============================================================================
    living_area: parseNumberWithFallback(rawDPE.surface_habitable_logement, 0),
    surface_habitable_immeuble: rawDPE.surface_habitable_immeuble
      ? parseNumberWithFallback(rawDPE.surface_habitable_immeuble, 0)
      : null,
    building_type: (rawDPE.type_batiment || '').toLowerCase().includes('appartement')
      ? 'appartement'
      : 'maison',
    annee_construction: rawDPE.annee_construction
      ? parseInt(rawDPE.annee_construction)
      : null,

    // Surfaces réelles ADEME (null si non renseignées → le calculateur utilisera un fallback estimé)
    surface_murs: rawDPE.surface_murs ? parseFloat(rawDPE.surface_murs) : null,
    surface_planchers_hauts: rawDPE.surface_planchers_hauts ? parseFloat(rawDPE.surface_planchers_hauts) : null,
    surface_planchers_bas: rawDPE.surface_planchers_bas ? parseFloat(rawDPE.surface_planchers_bas) : null,
    surface_baies_vitrees_total: (() => {
      const nord = parseFloat(rawDPE.surface_baies_vitrees_orientees_nord) || 0;
      const estOuest = parseFloat(rawDPE.surface_baies_vitrees_orientees_est_ouest) || 0;
      const sud = parseFloat(rawDPE.surface_baies_vitrees_orientees_sud) || 0;
      const total = nord + estOuest + sud;
      return total > 0 ? total : null;
    })(),
    
    // ============================================================================
    // ZONE CLIMATIQUE
    // ============================================================================
    climate_zone: rawDPE.climate_zone || rawDPE.zone_climatique || 'H2',
    
    // ============================================================================
    // ISOLATION
    // ============================================================================
    wall_insulation: normalizeQuality(rawDPE.qualite_isolation_murs),
    roof_insulation: normalizeQuality(
      rawDPE.qualite_isolation_plancher_haut_comble_perdu ||
      rawDPE.qualite_isolation_plancher_haut_comble_amenage ||
      rawDPE.qualite_isolation_plancher_haut_toit_terrasse
    ),
    floor_insulation: normalizeQuality(rawDPE['qualite_isolation_plancher bas']),
    windows_type: normalizeQuality(rawDPE.qualite_isolation_menuiseries),

    // Détecter le type de toiture depuis les données DPE
    has_flat_roof: !!(rawDPE.qualite_isolation_plancher_haut_toit_terrasse),
    has_attic: !!(rawDPE.qualite_isolation_plancher_haut_comble_perdu || rawDPE.qualite_isolation_plancher_haut_comble_amenage),

    // Champs originaux
    qualite_isolation_murs: rawDPE.qualite_isolation_murs || 'moyenne',
    qualite_isolation_menuiseries: rawDPE.qualite_isolation_menuiseries || 'moyenne',
    qualite_isolation_plancher_bas: rawDPE['qualite_isolation_plancher bas'] || 'moyenne',
    qualite_isolation_plancher_haut_comble_amenage: rawDPE.qualite_isolation_plancher_haut_comble_amenage || '',
    qualite_isolation_plancher_haut_comble_perdu: rawDPE.qualite_isolation_plancher_haut_comble_perdu || '',
    qualite_isolation_plancher_haut_toit_terrasse: rawDPE.qualite_isolation_plancher_haut_toit_terrasse || '',
    
    // ============================================================================
    // DÉPERDITIONS - CRITIQUES : undefined si absent !
    // ============================================================================
    deperditions_murs: parseNumberOrUndefined(rawDPE.deperditions_murs),
    deperditions_baies_vitrees: parseNumberOrUndefined(rawDPE.deperditions_baies_vitrees),
    deperditions_planchers_hauts: parseNumberOrUndefined(rawDPE.deperditions_planchers_hauts),
    deperditions_planchers_bas: parseNumberOrUndefined(rawDPE.deperditions_planchers_bas),
    deperditions_toiture: parseNumberOrUndefined(rawDPE.deperditions_planchers_hauts), // Même valeur
    deperditions_ponts_thermiques: parseNumberOrUndefined(rawDPE.deperditions_ponts_thermiques),
    deperditions_enveloppe: parseNumberOrUndefined(rawDPE.deperditions_enveloppe),
    deperditions_renouvellement_air: parseNumberOrUndefined(rawDPE.deperditions_renouvellement_air),

    // ============================================================================
    // CHAUFFAGE - GESTION DU DOUBLE-ENCODAGE JSON
    // ============================================================================
    heating_energy: (() => {
      const parsed = parseJSONField(rawDPE.heating_system);
      if (parsed && typeof parsed === 'object' && parsed.energy) {
        return parsed.energy;
      }
      return rawDPE.type_energie_principale_chauffage || 'inconnu';
    })(),
    heating_type: (() => {
      const parsed = parseJSONField(rawDPE.heating_system);
      if (parsed && typeof parsed === 'object' && parsed.type) {
        return parsed.type;
      }
      return rawDPE.type_generateur_chauffage_principal || 'inconnu';
    })(),
    heating_system: (() => {
      const parsed = parseJSONField(rawDPE.heating_system);
      if (parsed && typeof parsed === 'object') {
        return JSON.stringify(parsed);
      }
      return rawDPE.description_generateur_chauffage_n1_installation_n1 ||
             rawDPE.type_generateur_chauffage_principal ||
             'inconnu';
    })(),

    // Champs originaux
    type_energie_principale_chauffage: rawDPE.type_energie_principale_chauffage || 'inconnu',
    type_generateur_chauffage_principal: rawDPE.type_generateur_chauffage_principal || 'inconnu',
    description_generateur_chauffage_n1_installation_n1: rawDPE.description_generateur_chauffage_n1_installation_n1 || '',

    // ============================================================================
    // ECS - GESTION DU DOUBLE-ENCODAGE JSON
    // ============================================================================
    hot_water_energy: (() => {
      const parsed = parseJSONField(rawDPE.hot_water_system);
      if (parsed && typeof parsed === 'object' && parsed.energy) {
        return parsed.energy;
      }
      return rawDPE.type_energie_principale_ecs || 'inconnu';
    })(),
    hot_water_type: (() => {
      const parsed = parseJSONField(rawDPE.hot_water_system);
      if (parsed && typeof parsed === 'object' && parsed.type) {
        return parsed.type;
      }
      return rawDPE.type_generateur_chauffage_principal_ecs || 'inconnu';
    })(),

    // Champs originaux
    type_energie_principale_ecs: rawDPE.type_energie_principale_ecs || 'inconnu',
    type_generateur_chauffage_principal_ecs: rawDPE.type_generateur_chauffage_principal_ecs || 'inconnu',
    
    // ============================================================================
    // VENTILATION
    // ============================================================================
    ventilation_system: rawDPE.type_ventilation || 'inconnu',
    type_ventilation: rawDPE.type_ventilation || 'inconnu',
    
    // ============================================================================
    // CONSOMMATIONS
    // ============================================================================
    primary_energy_consumption: parseNumberWithFallback(rawDPE.conso_5_usages_par_m2_ep, 0),
    conso_5_usages_par_m2_ep: parseNumberWithFallback(rawDPE.conso_5_usages_par_m2_ep, 0),
    conso_5_usages_ef: parseNumberOrUndefined(rawDPE['conso_5 usages_ef'] || rawDPE.conso_5_usages_ef),
    conso_5_usages_par_m2_ef: parseNumberWithFallback(rawDPE['conso_5 usages_par_m2_ef'] || rawDPE.conso_5_usages_par_m2_ef, 0),
    
    conso_chauffage_ef: parseNumberOrUndefined(rawDPE.conso_chauffage_ef), // CRITIQUE
    conso_ecs_ef: parseNumberOrUndefined(rawDPE.conso_ecs_ef), // CRITIQUE
    
    besoin_chauffage: parseNumberOrUndefined(rawDPE.besoin_chauffage), // CRITIQUE
    besoin_ecs: parseNumberOrUndefined(rawDPE.besoin_ecs), // CRITIQUE
    
    // ============================================================================
    // ÉMISSIONS GES
    // ============================================================================
    greenhouse_gas_emissions: parseNumberWithFallback(
      rawDPE['emission_ges_5_usages par_m2'] ||
      rawDPE.emission_ges_5_usages_par_m2,
      0
    ),
    emission_ges_5_usages: parseNumberWithFallback(rawDPE.emission_ges_5_usages, 0),

    // Émissions par usage (en kgCO2/an total)
    emission_ges_chauffage: parseNumberOrUndefined(rawDPE.emission_ges_chauffage),
    emission_ges_ecs: parseNumberOrUndefined(rawDPE.emission_ges_ecs),
    emission_ges_eclairage: parseNumberOrUndefined(rawDPE.emission_ges_eclairage),
    emission_ges_auxiliaires: parseNumberOrUndefined(rawDPE.emission_ges_auxiliaires),

    // ============================================================================
    // CLASSES
    // ============================================================================
    energy_class: (rawDPE.etiquette_dpe || 'G').toUpperCase(),
    climate_class: (rawDPE.etiquette_ges || 'G').toUpperCase(),
    etiquette_dpe: (rawDPE.etiquette_dpe || 'G').toUpperCase(),
    etiquette_ges: (rawDPE.etiquette_ges || 'G').toUpperCase()
  };
}

/**
 * Détecte si le chauffage principal est électrique
 */
export function isChauffageElectrique(dpe: NormalizedDPEData): boolean {
  const energy = dpe.heating_energy.toLowerCase();
  const type = dpe.heating_type.toLowerCase();
  const system = dpe.heating_system.toLowerCase();
  
  const hasElectricEnergy = 
    energy.includes('électricité') || 
    energy.includes('electricite') ||
    energy.includes('electric');
  
  const hasElectricSystem = 
    type.includes('électrique') ||
    type.includes('electrique') ||
    type.includes('convecteur') ||
    type.includes('radiateur') ||
    type.includes('panneau rayonnant') ||
    system.includes('électrique') ||
    system.includes('electrique') ||
    system.includes('convecteur') ||
    system.includes('radiateur');
  
  return hasElectricEnergy || hasElectricSystem;
}

/**
 * Détecte si le chauffage est par radiateurs/convecteurs électriques
 */
export function isChauffageElectriqueRadiateurs(dpe: NormalizedDPEData): boolean {
  if (!isChauffageElectrique(dpe)) {
    return false;
  }
  
  const type = dpe.heating_type.toLowerCase();
  const system = dpe.heating_system.toLowerCase();
  
  // Exclure les PAC
  const isPAC = type.includes('pompe à chaleur') || 
                type.includes('pompe a chaleur') ||
                type.includes('pac') ||
                system.includes('pompe à chaleur') ||
                system.includes('pompe a chaleur');
  
  if (isPAC) {
    return false;
  }
  
  // Vérifier présence radiateurs/convecteurs
  const hasRadiators = 
    type.includes('convecteur') ||
    type.includes('radiateur') ||
    type.includes('panneau rayonnant') ||
    system.includes('convecteur') ||
    system.includes('radiateur') ||
    system.includes('panneau rayonnant');
  
  return hasRadiators;
}

/**
 * Détecte si le logement est un appartement
 */
export function isAppartement(dpe: NormalizedDPEData): boolean {
  if (dpe.building_type === 'appartement') {
    return true;
  }
  
  if (dpe.surface_habitable_immeuble && 
      dpe.surface_habitable_immeuble > dpe.living_area) {
    return true;
  }
  
  return false;
}

/**
 * Détecte si le chauffage est au gaz
 */
export function isChauffageGaz(dpe: NormalizedDPEData): boolean {
  const energy = dpe.heating_energy.toLowerCase();
  return energy.includes('gaz');
}

/**
 * Détecte si l'ECS est électrique
 */
export function isECSElectrique(dpe: NormalizedDPEData): boolean {
  const energy = dpe.hot_water_energy.toLowerCase();
  return energy.includes('électricité') || energy.includes('electricite');
}